// src/Groups.js

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import UserBadge from "./UserBadge";
import fetchWithAuth from "./fetchWithAuth";
import "./style-new.css";

import {
  FaHome,
  FaPlus,
  FaFolderOpen,
  FaFutbol,
  FaTrash,
  FaSignOutAlt,
} from "react-icons/fa";

const Groups = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [membersByGroup, setMembersByGroup] = useState({});
  const [adminGroups, setAdminGroups] = useState([]);
  const navigate = useNavigate();

  const fetchGroups = async () => {
    try {
      const response = await fetchWithAuth("/api/groups/my");
      const data = await response.json();

      setGroups(data.groups);

      const adminGroupIds = data.groups
        .filter((group) => group.is_admin)
        .map((group) => group.id);
      setAdminGroups(adminGroupIds);

      for (const group of data.groups) {
        fetchMembers(group.id);
      }
    } catch (error) {
      console.error("Error fetching groups:", error);
      setError("Failed to load groups. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async (groupId) => {
    try {
      const response = await fetchWithAuth(`/api/groups/${groupId}/members`);
      const data = await response.json();
      setMembersByGroup((prev) => ({
        ...prev,
        [groupId]: data.members,
      }));
    } catch (error) {
      console.error("Error fetching members:", error);
    }
  };

  const deleteGroup = async (groupId) => {
    if (!window.confirm("Are you sure you want to delete this group?")) return;
    try {
      const response = await fetchWithAuth(`/api/groups/${groupId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || "Failed to delete group.");
      }

      setGroups(groups.filter((g) => g.id !== groupId));
      alert("Group deleted successfully.");
    } catch (error) {
      console.error("Error deleting group:", error);
      alert("Failed to delete the group.");
    }
  };

  const handleLeaveGroup = async (groupId) => {
    if (!window.confirm("Are you sure you want to leave this group?")) return;
    try {
      const response = await fetchWithAuth(`/api/groups/${groupId}/leave`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || "Failed to leave the group.");
      }

      const data = await response.json();
      alert(data.message || "You have left the group.");
      fetchGroups();
    } catch (error) {
      console.error("Error leaving group:", error);
      alert(error.message || "Failed to leave the group.");
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  return (
    <div className="page-container">
      <UserBadge />
      <div className="center-content">
        <h1 className="welcome-heading">My Groups</h1>

        {error && <div className="error-text">{error}</div>}

        <div className="button-group" style={{ marginBottom: "30px" }}>
          <button
            className="btn btn-primary"
            onClick={() => navigate("/")}
          >
            <FaHome />
            Home
          </button>
          <button
            className="btn btn-primary"
            onClick={() => navigate("/groups/create-group")}
          >
            <FaPlus />
            Create Group
          </button>
        </div>

        {loading ? (
          <div>Loading groups...</div>
        ) : groups.length > 0 ? (
          <ul className="no-list-style">
            {groups.map((group) => (
              <li key={group.id} style={{ marginBottom: "30px" }}>
                <strong>{group.name}</strong>
                <div style={{ marginTop: "10px" }}>
                  <button
                    className="btn pastel-blue small-btn"
                    onClick={() => navigate(`/group/${group.id}`)}
                  >
                    <FaFolderOpen />
                    Open Group
                  </button>
                  {adminGroups.includes(group.id) && (
                    <>
                      <button
                        className="btn pastel-green small-btn"
                        onClick={() =>
                          navigate("/create-game", {
                            state: {
                              group_id: group.id,
                              group_name: group.name,
                            },
                          })
                        }
                      >
                        <FaFutbol />
                        Create Game
                      </button>
                      <button
                        className="btn pastel-red small-btn"
                        onClick={() => deleteGroup(group.id)}
                      >
                        <FaTrash />
                        Delete Group
                      </button>
                    </>
                  )}
                  <button
                    className="btn pastel-yellow small-btn"
                    onClick={() => handleLeaveGroup(group.id)}
                  >
                    <FaSignOutAlt />
                    Leave Group
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div>You are not part of any groups yet.</div>
        )}
      </div>
    </div>
  );
};

export default Groups;
